<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PurchaseProjectItemRow from './PurchaseProjectItemRow.vue';
import { mockProducts } from './story/fixtures.js';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['update-item', 'remove-item', 'add-product']);

const { t } = useI18n();

const estimatedTotal = computed(() => {
  return props.items.reduce((sum, item) => {
    const product = mockProducts.find(p => p.id === item.productId);
    return sum + (product?.price ?? 0) * (item.quantity ?? 1);
  }, 0);
});

const formattedTotal = computed(() =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    estimatedTotal.value
  )
);

const sortedItems = computed(() =>
  [...props.items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
);
</script>

<template>
  <div class="flex flex-col gap-0">
    <div class="flex items-center justify-between px-4 py-3">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('PURCHASE_PROJECTS.DETAIL.SELECTION_TITLE') }}
      </span>
      <button
        type="button"
        class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="emit('add-product')"
      >
        <span class="i-lucide-plus size-3.5" />
        {{ t('PURCHASE_PROJECTS.DETAIL.ADD_PRODUCT') }}
      </button>
    </div>

    <div v-if="sortedItems.length > 0" class="px-4">
      <PurchaseProjectItemRow
        v-for="item in sortedItems"
        :key="item.productId + item.variantId"
        :item="item"
        @update="updatedItem => emit('update-item', updatedItem)"
        @remove="removedItem => emit('remove-item', removedItem)"
      />
    </div>

    <p v-else class="px-4 py-6 text-sm text-center text-n-slate-11">
      {{ t('PURCHASE_PROJECTS.ITEMS.EMPTY_STATE') }}
    </p>

    <div
      v-if="sortedItems.length > 0"
      class="flex items-center justify-between px-4 py-3"
    >
      <span class="text-sm font-medium text-n-slate-11">
        {{ t('PURCHASE_PROJECTS.DETAIL.ESTIMATED_TOTAL') }}
      </span>
      <span class="text-base font-semibold text-n-slate-12">
        {{ formattedTotal }}
      </span>
    </div>
  </div>
</template>
